import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/shared/AuthContext';
import { useTheme } from '../context/shared/ThemeContext';

const Signup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const { themeClasses } = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    try {
      setError('');
      setLoading(true);
      await signUp(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError('Failed to create an account');
      console.error('Signup error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen py-12 px-4 sm:px-6 lg:px-8 ${themeClasses.mainBg}`}>
      <div className="max-w-md w-full mx-auto">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <span role="img" aria-label="brain" className="text-4xl mr-3">🧠</span>
            <h1 className={`text-3xl font-bold ${themeClasses.primaryText}`}>
              Create Account
            </h1>
          </div>
          <p className={`mt-2 text-sm ${themeClasses.secondaryText}`}>
            Join Mentra and start your journey
          </p>
        </div>

        <div className={`${themeClasses.cardBg} rounded-lg shadow-sm px-8 py-8`}>
          {error && (
            <div className="mb-4 p-3 rounded bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className={`block text-sm font-medium ${themeClasses.primaryText}`}>
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`
                  mt-1 block w-full rounded-md
                  border-gray-300 dark:border-gray-600
                  bg-white dark:bg-gray-700
                  text-gray-900 dark:text-gray-100
                  focus:ring-blue-500 focus:border-blue-500
                  shadow-sm
                `}
              />
            </div>

            <div>
              <label htmlFor="password" className={`block text-sm font-medium ${themeClasses.primaryText}`}>
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`
                  mt-1 block w-full rounded-md
                  border-gray-300 dark:border-gray-600
                  bg-white dark:bg-gray-700
                  text-gray-900 dark:text-gray-100
                  focus:ring-blue-500 focus:border-blue-500
                  shadow-sm
                `}
              />
            </div>

            <div>
              <label htmlFor="confirm-password" className={`block text-sm font-medium ${themeClasses.primaryText}`}>
                Confirm Password
              </label>
              <input
                id="confirm-password"
                type="password"
                required
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`
                  mt-1 block w-full rounded-md
                  border-gray-300 dark:border-gray-600
                  bg-white dark:bg-gray-700
                  text-gray-900 dark:text-gray-100
                  focus:ring-blue-500 focus:border-blue-500
                  shadow-sm
                `}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`
                w-full flex justify-center py-2 px-4 rounded-md
                ${loading ? 'opacity-70 cursor-not-allowed' : ''}
                bg-blue-600 hover:bg-blue-700
                text-white font-medium
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                transition-colors
              `}
            >
              {loading ? 'Creating Account...' : 'Sign Up'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className={`text-sm ${themeClasses.secondaryText}`}>
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
                Log in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup; 